/**
 * Public-route classification shared between server-side middleware and
 * client-side auth bootstrap. Single source of truth — `middleware.ts`
 * gates SSR access on this list, and `auth-context.tsx` keys its
 * session-recovery decision on the same list (only protected pages attempt
 * `recoverSession()` when the client comes up empty in a new tab).
 *
 * Keeping these in lockstep prevents the new-tab spinner-hang class of bug:
 * a tab that the server admitted (cookie present) but the client cannot
 * bootstrap needs the same definition of "this is a protected surface" on
 * both sides.
 */

export const PUBLIC_ROUTES = [
  '/login',
  '/logout',
  '/forgot-password',
  '/reset-password',
  '/resend-invitation',
  '/auth/action',
] as const;

export function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.some((route) => pathname.startsWith(route));
}

/**
 * Clamp a `?redirect=` target to a same-origin absolute path (issue #822).
 *
 * Rejects external URLs, protocol-relative `//host` targets, and bounces back
 * to /login — any of which could re-open the logout→login loop or an open
 * redirect. Everything else falls back to /dashboard.
 *
 * gh #1758: moved here from app/login/page.tsx, which was the only caller that
 * sanitized. app/login/[slug]/page.tsx read `searchParams.get('redirect')` raw
 * and handed it to the same GcipLoginCard, so `/login/acme?redirect=<external>`
 * was navigable — and post-login navigation is now a `window.location.href`
 * assignment, which follows an absolute URL off-origin without complaint.
 * One shared implementation, used by both routes.
 */
export function sanitizeRedirect(raw: string | null | undefined): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return '/dashboard';
  }
  if (raw === '/login' || raw.startsWith('/login?') || raw.startsWith('/login/')) {
    return '/dashboard';
  }
  return raw;
}
